import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PeriodName } from "@/models";
import { apartmentsSlice } from "@/store/slices/apartments";
import { bookingManagementApi } from "@/store/booking-management-api";

interface UserSettings {
    selectedYear: number;
    selectedPeriod: PeriodName;
    selectedOwner: number;
    selectedGroup: number;
    selectedApartment: number;
    aggregateMethod: "sum" | "avg";
}

const loadFromLocalStorage = (): UserSettings => ({
    selectedYear: parseInt(localStorage.getItem("selectedYear") || new Date().getFullYear().toString()),
    selectedPeriod: (localStorage.getItem("selectedPeriod") as PeriodName) || PeriodName.Year,
    selectedOwner: parseInt(localStorage.getItem("selectedOwner") || "0"),
    selectedGroup: parseInt(localStorage.getItem("selectedGroup") || "0"),
    selectedApartment: parseInt(localStorage.getItem("selectedApartment") || "0"),
    aggregateMethod: (localStorage.getItem("aggregateMethod") as "sum" | "avg") || "sum",
});

const initialState: UserSettings = loadFromLocalStorage();

const userSettingsSlice = createSlice({
    name: "userSettings",
    initialState,
    reducers: {
        setSelectedYear: (state, action: PayloadAction<number>) => {
            state.selectedYear = action.payload;
            localStorage.setItem("selectedYear", action.payload.toString());
        },
        setSelectedPeriod: (state, action: PayloadAction<PeriodName>) => {
            state.selectedPeriod = action.payload;
            localStorage.setItem("selectedPeriod", action.payload);
        },
        setSelectedOwner: (state, action: PayloadAction<number>) => {
            state.selectedOwner = action.payload;
            localStorage.setItem("selectedOwner", action.payload.toString());
        },
        setSelectedGroup: (state, action: PayloadAction<number>) => {
            state.selectedGroup = action.payload;
            localStorage.setItem("selectedGroup", action.payload.toString());
        },
        setSelectedApartment: (state, action: PayloadAction<number>) => {
            state.selectedApartment = action.payload;
            localStorage.setItem("selectedApartment", action.payload.toString());
        },
        setAggregateMethod: (state, action: PayloadAction<"sum" | "avg">) => {
            state.aggregateMethod = action.payload;
            localStorage.setItem("aggregateMethod", action.payload);
        },
    },
    extraReducers: (builder) => {
        // Handle apartments response
        builder.addMatcher(apartmentsSlice.endpoints.getApartments.matchFulfilled, (state, action) => {
            const apartments = Object.values(action.payload.entities);
            if (apartments.length > 0 && (state.selectedApartment === 0 || !apartments.some((apt) => apt.id === state.selectedApartment))) {
                const firstApartment = [...apartments].sort((a, b) => a.name.localeCompare(b.name))[0];
                state.selectedApartment = firstApartment.id;
                localStorage.setItem("selectedApartment", firstApartment.id.toString());
            }
        });

        // Handle owners response
        builder.addMatcher(bookingManagementApi.endpoints.getApartmentOwners.matchFulfilled, (state, action) => {
            const owners = action.payload;
            if (owners.length > 0 && (state.selectedOwner === 0 || !owners.some((owner) => owner.id === state.selectedOwner))) {
                const firstOwner = [...owners].sort((a, b) => a.fullName.localeCompare(b.fullName))[0];
                state.selectedOwner = firstOwner.id;
                localStorage.setItem("selectedOwner", firstOwner.id.toString());
            }
        });

        // Handle groups response
        builder.addMatcher(bookingManagementApi.endpoints.getGroups.matchFulfilled, (state, action) => {
            const groups = action.payload;
            if (groups.length > 0 && (state.selectedGroup === 0 || !groups.some((group) => group.id === state.selectedGroup))) {
                const firstGroup = [...groups].sort((a, b) => a.name.localeCompare(b.name))[0];
                state.selectedGroup = firstGroup.id;
                localStorage.setItem("selectedGroup", firstGroup.id.toString());
            }
        });
    },
});

export const actions = userSettingsSlice.actions;

export default userSettingsSlice.reducer;
